"use client";

import * as DialogPrimitive from "@radix-ui/react-dialog";
import * as React from "react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useI18n } from "@/lib/i18n";
import { useUser } from "@/lib/context/user-context";
import { cn } from "@/lib/utils";
import { CheckIcon, X, Loader2 } from "lucide-react";

const Dialog = DialogPrimitive.Root;

const DialogTrigger = DialogPrimitive.Trigger;

const DialogPortal = DialogPrimitive.Portal;

const DialogClose = DialogPrimitive.Close;

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      "fixed inset-0 z-[101] bg-black/80 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
      className,
    )}
    {...props}
  />
));
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content>
>(({ className, children, ...props }, ref) => (
  <DialogPortal>
    <DialogOverlay />
  <DialogPrimitive.Content
    ref={ref}
      className={cn(
        "fixed left-1/2 top-1/2 z-[101] grid max-h-[calc(100%-3rem)] w-full max-w-[calc(100%-2rem)] -translate-x-1/2 -translate-y-1/2 gap-4 overflow-y-auto rounded-[32px] border border-border/80 bg-background/95 p-6 shadow-2xl shadow-black/10 backdrop-blur supports-[backdrop-filter]:backdrop-blur transition-all duration-150 data-[state=closed]:opacity-0 data-[state=open]:opacity-100 sm:max-w-[600px] sm:rounded-[38px]",
        className,
      )}
    {...props}
  >
      {children}
      <DialogPrimitive.Close className="group absolute right-3 top-3 flex size-7 items-center justify-center rounded-lg outline-offset-2 transition-colors focus-visible:outline focus-visible:outline-2 focus-visible:outline-ring/70 disabled:pointer-events-none">
        <X className="size-4 opacity-60 transition-opacity group-hover:opacity-100" strokeWidth={2} />
        <span className="sr-only">Close</span>
      </DialogPrimitive.Close>
    </DialogPrimitive.Content>
  </DialogPortal>
));
DialogContent.displayName = DialogPrimitive.Content.displayName;

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div className={cn("flex flex-col space-y-1.5 text-center sm:text-left", className)} {...props} />
);
DialogHeader.displayName = "DialogHeader";

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn("flex flex-col-reverse gap-2 sm:flex-row sm:justify-end sm:gap-3", className)}
    {...props}
  />
);
DialogFooter.displayName = "DialogFooter";

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn("text-lg font-semibold tracking-tight", className)}
    {...props}
  />
));
DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn("text-sm text-muted-foreground", className)}
    {...props}
  />
));
DialogDescription.displayName = DialogPrimitive.Description.displayName;

type EnglishLevel = "A2" | "B1" | "B2" | "C1";
type SessionFrequency = "3_per_week" | "5_per_week" | "7_per_week";
type ExplanationPreference =
  | "show_key_vocab"
  | "show_phrases"
  | "show_sentence_translation"
  | "focus_pronunciation_shadowing";

export interface PersonaOnboardingResult {
  profile: {
    basic: {
      english_level?: EnglishLevel;
      target_difficulty_range?: [EnglishLevel, EnglishLevel];
    };
  };
  goals: string[];
  topics: string[];
  format_preferences: string[];
  session_preferences: {
    length_minutes?: number;
    frequency?: SessionFrequency | null;
  };
  language_preferences?: {
    native_language?: string | null;
    target_language?: string | null;
  };
  subtitles: {
    with_subtitles: boolean;
    native_language_support: boolean;
    explanation_preferences: ExplanationPreference[];
    native_language?: string | null;
  };
}

export interface PersonaOnboardingDialogProps {
  /**
   * Custom trigger button. Leave empty to control the dialog externally.
   */
  trigger?: React.ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onComplete?: (persona: PersonaOnboardingResult) => void;
  defaultValues?: Partial<PersonaOnboardingResult>;
}

interface PersonaBuilderState {
  goals: string[];
  topics: string[];
  englishLevel?: EnglishLevel;
  targetDifficultyRange?: [EnglishLevel, EnglishLevel];
  formats: string[];
  sessionLength?: number;
  sessionFrequency?: SessionFrequency | null;
  languagePreferences: {
    nativeLanguage: string | null;
    targetLanguage: string;
  };
  subtitles: {
    withSubtitles: boolean;
    nativeLanguageSupport: boolean;
    explanationPreferences: ExplanationPreference[];
    nativeLanguage?: string | null;
  };
}

const TARGET_DIFFICULTY_MAP: Record<EnglishLevel, [EnglishLevel, EnglishLevel]> = {
  A2: ["A2", "B1"],
  B1: ["B1", "B2"],
  B2: ["B2", "C1"],
  C1: ["B2", "C1"],
};

const GOAL_OPTION_DEFS = [
  { value: "everyday_conversation", labelKey: "persona.goals.everyday", fallback: "Everyday conversation" },
  { value: "watch_youtube", labelKey: "persona.goals.watchYoutube", fallback: "Watch YouTube without subtitles" },
  { value: "job_interview", labelKey: "persona.goals.jobInterview", fallback: "Job interviews & career" },
  { value: "study_exam", labelKey: "persona.goals.studyExam", fallback: "Study / exams (IELTS, TOEFL…)" },
  { value: "travel_abroad", labelKey: "persona.goals.travelAbroad", fallback: "Travel & living abroad" },
] as const;

const TOPIC_OPTION_DEFS = [
  { value: "daily_vlog", labelKey: "persona.topics.dailyVlog", fallback: "Daily vlog & lifestyle" },
  { value: "ai_tech", labelKey: "persona.topics.aiTech", fallback: "Tech & AI" },
  { value: "career", labelKey: "persona.topics.career", fallback: "Career & self-improvement" },
  { value: "comedy", labelKey: "persona.topics.comedy", fallback: "Comedy & entertainment" },
  { value: "movies_tv", labelKey: "persona.topics.moviesTv", fallback: "Movies & TV shows" },
  { value: "news", labelKey: "persona.topics.news", fallback: "News & current events" },
  { value: "gaming", labelKey: "persona.topics.gaming", fallback: "Gaming" },
  { value: "education", labelKey: "persona.topics.education", fallback: "Education & tutorials" },
] as const;

const ENGLISH_LEVEL_OPTION_DEFS = [
  {
    value: "A2",
    labelKey: "persona.levels.a2.title",
    fallback: "Beginner – I know some words but can’t follow most videos.",
    descriptionKey: "persona.levels.a2.description",
    descriptionFallback: "Basic CEFR level",
  },
  {
    value: "B1",
    labelKey: "persona.levels.b1.title",
    fallback: "Lower-intermediate – I need subtitles to follow most content.",
    descriptionKey: "persona.levels.b1.description",
    descriptionFallback: "Lower-intermediate",
  },
  {
    value: "B2",
    labelKey: "persona.levels.b2.title",
    fallback: "Intermediate – With subtitles I understand 70–80%.",
    descriptionKey: "persona.levels.b2.description",
    descriptionFallback: "Upper-intermediate",
  },
  {
    value: "C1",
    labelKey: "persona.levels.c1.title",
    fallback: "Advanced – I can follow most videos without subtitles.",
    descriptionKey: "persona.levels.c1.description",
    descriptionFallback: "Advanced",
  },
] as const satisfies readonly {
  value: EnglishLevel;
  labelKey: string;
  fallback: string;
  descriptionKey: string;
  descriptionFallback: string;
}[];

const FORMAT_OPTION_DEFS = [
  { value: "vlog", labelKey: "persona.formats.vlog", fallback: "Vlogs / daily life" },
  { value: "interview", labelKey: "persona.formats.interview", fallback: "Interviews & podcasts" },
  { value: "podcast", labelKey: "persona.formats.podcast", fallback: "Podcasts / audio stories" },
  { value: "tutorial", labelKey: "persona.formats.tutorial", fallback: "Explainer / tutorials" },
  { value: "shorts", labelKey: "persona.formats.shorts", fallback: "Short clips / highlights" },
  { value: "movie_clip", labelKey: "persona.formats.movieClip", fallback: "Movie / TV show scenes" },
] as const;

const SESSION_LENGTH_OPTION_DEFS = [
  { value: 10, labelKey: "persona.sessionLength.ten", fallback: "5–10 minutes" },
  { value: 20, labelKey: "persona.sessionLength.twenty", fallback: "10–20 minutes" },
  { value: 30, labelKey: "persona.sessionLength.thirty", fallback: "20–30 minutes" },
  { value: 45, labelKey: "persona.sessionLength.fortyFive", fallback: "30+ minutes" },
] as const;

const SESSION_FREQUENCY_OPTION_DEFS = [
  { value: "3_per_week", labelKey: "persona.sessionFrequency.three", fallback: "A few times per week" },
  { value: "5_per_week", labelKey: "persona.sessionFrequency.five", fallback: "Almost every day" },
  { value: "7_per_week", labelKey: "persona.sessionFrequency.seven", fallback: "Every day" },
] as const satisfies readonly { labelKey: string; fallback: string; value: SessionFrequency }[];

const EXPLANATION_PREFERENCE_DEFS = [
  {
    value: "show_key_vocab",
    labelKey: "persona.explanations.keyVocab",
    fallback: "Show key vocabulary for each video",
  },
  {
    value: "show_phrases",
    labelKey: "persona.explanations.showPhrases",
    fallback: "Highlight useful phrases & expressions",
  },
  {
    value: "show_sentence_translation",
    labelKey: "persona.explanations.translateSentences",
    fallback: "Translate tricky sentences into your native language",
  },
  {
    value: "focus_pronunciation_shadowing",
    labelKey: "persona.explanations.pronunciation",
    fallback: "Focus on pronunciation and shadowing",
  },
] as const satisfies readonly { labelKey: string; fallback: string; value: ExplanationPreference }[];

const NATIVE_LANGUAGE_OPTION_DEFS = [
  { value: "en", labelKey: "persona.languages.native.en", fallback: "English" },
  { value: "zh", labelKey: "persona.languages.native.zh", fallback: "Chinese" },
  { value: "es", labelKey: "persona.languages.native.es", fallback: "Spanish" },
  { value: "ja", labelKey: "persona.languages.native.ja", fallback: "Japanese" },
  { value: "ko", labelKey: "persona.languages.native.ko", fallback: "Korean" },
  { value: "fr", labelKey: "persona.languages.native.fr", fallback: "French" },
  { value: "de", labelKey: "persona.languages.native.de", fallback: "German" },
  { value: "pt", labelKey: "persona.languages.native.pt", fallback: "Portuguese" },
  { value: "ru", labelKey: "persona.languages.native.ru", fallback: "Russian" },
] as const;

const TARGET_LANGUAGE_OPTION_DEFS = [
  { value: "en", labelKey: "persona.languages.target.en", fallback: "English" },
  { value: "zh", labelKey: "persona.languages.target.zh", fallback: "Chinese" },
  { value: "es", labelKey: "persona.languages.target.es", fallback: "Spanish" },
  { value: "ja", labelKey: "persona.languages.target.ja", fallback: "Japanese" },
  { value: "ko", labelKey: "persona.languages.target.ko", fallback: "Korean" },
  { value: "fr", labelKey: "persona.languages.target.fr", fallback: "French" },
  { value: "de", labelKey: "persona.languages.target.de", fallback: "German" },
  { value: "pt", labelKey: "persona.languages.target.pt", fallback: "Portuguese" },
  { value: "ru", labelKey: "persona.languages.target.ru", fallback: "Russian" },
] as const;

const STEP_METADATA_DEFS = [
  {
    eyebrowKey: "persona.steps.languages.eyebrow",
    eyebrowFallback: "Step 1 · Languages",
    titleKey: "persona.steps.languages.title",
    titleFallback: "What are your native and target languages?",
    descriptionKey: "persona.steps.languages.description",
    descriptionFallback: "Tell us your preferred languages so we can personalize translations.",
  },
  {
    eyebrowKey: "persona.steps.level.eyebrow",
    eyebrowFallback: "Step 2 · Target Level",
    titleKey: "persona.steps.level.title",
    titleFallback: "How would you rate your target language level?",
    descriptionKey: "persona.steps.level.description",
    descriptionFallback: "Just a rough self-assessment, no test needed.",
  },
  {
    eyebrowKey: "persona.steps.goals.eyebrow",
    eyebrowFallback: "Step 3 · Goals",
    titleKey: "persona.steps.goals.title",
    titleFallback: "What’s your main goal for learning your target language?",
    descriptionKey: "persona.steps.goals.description",
    descriptionFallback: "Pick the goals that best match your journey. You can adjust them anytime.",
  },
  {
    eyebrowKey: "persona.steps.topics.eyebrow",
    eyebrowFallback: "Step 4 · Topics / Interests",
    titleKey: "persona.steps.topics.title",
    titleFallback: "What kind of videos do you enjoy the most?",
    descriptionKey: "persona.steps.topics.description",
    descriptionFallback: "We’ll recommend English videos based on these topics.",
  },
  {
    eyebrowKey: "persona.steps.formats.eyebrow",
    eyebrowFallback: "Step 5 · Video Formats",
    titleKey: "persona.steps.formats.title",
    titleFallback: "What kind of video format do you like?",
    descriptionKey: "persona.steps.formats.description",
    descriptionFallback: "Choose the styles you enjoy. We’ll mix them in your recommendations.",
  },
  {
    eyebrowKey: "persona.steps.session.eyebrow",
    eyebrowFallback: "Step 6 · Session Length",
    titleKey: "persona.steps.session.title",
    titleFallback: "How long do you usually have for one session?",
    descriptionKey: "persona.steps.session.description",
    descriptionFallback: "We’ll match video length to your schedule.",
  },
  {
    eyebrowKey: "persona.steps.subtitles.eyebrow",
    eyebrowFallback: "Step 7 · Subtitles & Explanation",
    titleKey: "persona.steps.subtitles.title",
    titleFallback: "How should we support your learning?",
    descriptionKey: "persona.steps.subtitles.description",
    descriptionFallback: "Choose the subtitles and explanations you prefer.",
  },
] as const;

const toggleListValue = (list: string[], value: string) => {
  if (list.includes(value)) {
    return list.filter((item) => item !== value);
  }
  return [...list, value];
};

const buildStateFromDefaults = (
  defaults?: Partial<PersonaOnboardingResult>,
): PersonaBuilderState => {
  const englishLevel = defaults?.profile?.basic?.english_level;
  const existingRange = defaults?.profile?.basic?.target_difficulty_range;

  return {
    goals: defaults?.goals ?? [],
    topics: defaults?.topics ?? [],
    englishLevel,
    targetDifficultyRange:
      existingRange ?? (englishLevel ? TARGET_DIFFICULTY_MAP[englishLevel] : undefined),
    formats: defaults?.format_preferences ?? [],
    sessionLength: defaults?.session_preferences?.length_minutes,
    sessionFrequency: defaults?.session_preferences?.frequency ?? null,
    languagePreferences: {
      nativeLanguage:
        defaults?.language_preferences?.native_language ??
        defaults?.subtitles?.native_language ??
        null,
      targetLanguage: defaults?.language_preferences?.target_language ?? "en",
    },
    subtitles: {
      withSubtitles: defaults?.subtitles?.with_subtitles ?? true,
      nativeLanguageSupport: defaults?.subtitles?.native_language_support ?? false,
      explanationPreferences: defaults?.subtitles?.explanation_preferences ?? [],
      nativeLanguage:
        defaults?.subtitles?.native_language ??
        defaults?.language_preferences?.native_language ??
        null,
    },
  };
};

const buildPayload = (state: PersonaBuilderState): PersonaOnboardingResult => ({
  profile: {
    basic: {
      english_level: state.englishLevel,
      target_difficulty_range: state.targetDifficultyRange,
    },
  },
  goals: state.goals,
  topics: state.topics,
  format_preferences: state.formats,
  session_preferences: {
    length_minutes: state.sessionLength,
    frequency: state.sessionFrequency ?? null,
  },
  language_preferences: {
    native_language: state.languagePreferences.nativeLanguage || null,
    target_language: state.languagePreferences.targetLanguage ?? null,
  },
  subtitles: {
    with_subtitles: state.subtitles.withSubtitles,
    native_language_support: state.subtitles.nativeLanguageSupport,
    native_language:
      state.languagePreferences.nativeLanguage ||
      state.subtitles.nativeLanguage ||
      null,
    explanation_preferences: state.subtitles.explanationPreferences,
  },
});

interface SelectableCardProps {
  label: string;
  description?: string;
  isSelected: boolean;
  onClick: () => void;
  badge?: React.ReactNode;
  layout?: "vertical" | "horizontal";
}

const SelectableCard = ({
  label,
  description,
  isSelected,
  onClick,
  badge,
  layout = "vertical",
}: SelectableCardProps) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={isSelected}
    className={cn(
      "group relative w-full rounded-[28px] border px-3.5 py-2 text-left transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-black focus-visible:ring-offset-2 focus-visible:ring-offset-background",
      isSelected
        ? "border-black bg-black/5 shadow-lg shadow-black/20"
        : "border-border/70 bg-background/80 hover:-translate-y-0.5 hover:border-black/60 hover:bg-muted/40",
    )}
  >
    <div className={cn("flex items-start gap-3", layout === "horizontal" ? "flex-row" : "flex-col")}>
      <div className="flex-1 space-y-1">
        <div className="flex items-start gap-2">
          <p className="text-sm font-medium text-foreground flex-1">{label}</p>
          {badge ? <div className="shrink-0 self-start">{badge}</div> : null}
        </div>
        {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
      </div>
      <span
        className={cn(
          "pointer-events-none absolute right-3 bottom-3 inline-flex size-5 items-center justify-center rounded-full bg-black text-white transition-opacity",
          isSelected ? "opacity-100" : "opacity-0",
        )}
      >
        <CheckIcon size={14} strokeWidth={3} />
      </span>
    </div>
  </button>
);

const PersonaOnboardingDialog = ({
  trigger,
  open,
  onOpenChange,
  onComplete,
  defaultValues,
}: PersonaOnboardingDialogProps) => {
  const { t } = useI18n();
  const [internalOpen, setInternalOpen] = React.useState(false);
  const [step, setStep] = React.useState(1);
  const [isSaving, setIsSaving] = React.useState(false);
  const [state, setState] = React.useState<PersonaBuilderState>(() =>
    buildStateFromDefaults(defaultValues),
  );
  const stepMetadata = React.useMemo(
    () =>
      STEP_METADATA_DEFS.map((def) => ({
        eyebrow: t(def.eyebrowKey, def.eyebrowFallback),
        title: t(def.titleKey, def.titleFallback),
        description: t(def.descriptionKey, def.descriptionFallback),
      })),
    [t],
  );
  const goalOptions = React.useMemo(
    () =>
      GOAL_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const topicOptions = React.useMemo(
    () =>
      TOPIC_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const englishLevelOptions = React.useMemo(
    () =>
      ENGLISH_LEVEL_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
        description: t(option.descriptionKey, option.descriptionFallback),
      })),
    [t],
  );
  const formatOptions = React.useMemo(
    () =>
      FORMAT_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const sessionLengthOptions = React.useMemo(
    () =>
      SESSION_LENGTH_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
        description: t("persona.sessionLength.description", "{minutes} min target").replace(
          "{minutes}",
          String(option.value),
        ),
      })),
    [t],
  );
  const sessionFrequencyOptions = React.useMemo(
    () =>
      SESSION_FREQUENCY_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const explanationPreferences = React.useMemo(
    () =>
      EXPLANATION_PREFERENCE_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const nativeLanguageOptions = React.useMemo(
    () =>
      NATIVE_LANGUAGE_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const targetLanguageOptions = React.useMemo(
    () =>
      TARGET_LANGUAGE_OPTION_DEFS.map((option) => ({
        value: option.value,
        label: t(option.labelKey, option.fallback),
      })),
    [t],
  );
  const { user } = useUser();
  const userId = user?.id;
  const totalSteps = stepMetadata.length;

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  React.useEffect(() => {
    setState(buildStateFromDefaults(defaultValues));
  }, [defaultValues]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (!isControlled) {
      setInternalOpen(nextOpen);
    }
    if (!nextOpen) {
      setStep(1);
    }
    onOpenChange?.(nextOpen);
  };

  const persistPersonaProfile = React.useCallback(
    async (profile: PersonaOnboardingResult) => {
      if (!userId) return;
      setIsSaving(true);
      try {
        // Save personaProfile
        const personaResponse = await fetch("/api/user-settings", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            userId,
            settingKey: "personaProfile",
            value: profile,
          }),
        });

        if (!personaResponse.ok) {
          throw new Error(`Failed to save personaProfile: ${personaResponse.status}`);
        }

        // Also update languageSetting with native_language
        const nativeLanguage = profile.language_preferences?.native_language || profile.subtitles?.native_language;
        if (nativeLanguage) {
          const languageResponse = await fetch("/api/user-settings", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              settingKey: "languageSetting",
              value: nativeLanguage,
            }),
          });

          if (!languageResponse.ok) {
            console.warn(
              `[PersonaOnboardingDialog] Failed to update languageSetting: ${languageResponse.status}`,
            );
            // Don't throw - personaProfile was saved successfully
          }
        }

        // Also save target_language to Redis
        const targetLanguage = profile.language_preferences?.target_language;
        if (targetLanguage) {
          const targetLanguageResponse = await fetch("/api/user-settings", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              settingKey: "targetLanguageSetting",
              value: targetLanguage,
            }),
          });

          if (!targetLanguageResponse.ok) {
            console.warn(
              `[PersonaOnboardingDialog] Failed to update targetLanguageSetting: ${targetLanguageResponse.status}`,
            );
            // Don't throw - personaProfile was saved successfully
          }
        }

        // Also save english_level to cefrLevel
        const englishLevel = profile.profile?.basic?.english_level;
        if (englishLevel) {
          const cefrLevelResponse = await fetch("/api/user-settings", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              userId,
              settingKey: "cefrLevel",
              value: englishLevel,
            }),
          });

          if (!cefrLevelResponse.ok) {
            console.warn(
              `[PersonaOnboardingDialog] Failed to update cefrLevel: ${cefrLevelResponse.status}`,
            );
            // Don't throw - personaProfile was saved successfully
          }
        }
      } catch (error) {
        console.error("[PersonaOnboardingDialog] persist persona error", error);
        throw error; // Re-throw to allow caller to handle
      } finally {
        setIsSaving(false);
      }
    },
    [userId],
  );

  const handleContinue = async () => {
    if (isSaving) return; // Prevent multiple clicks while saving

    if (step === totalSteps) {
      const payload = buildPayload(state);
      await persistPersonaProfile(payload);
      onComplete?.(payload);
      handleOpenChange(false);
      return;
    }

    setStep((prev) => Math.min(totalSteps, prev + 1));
  };

  const handleBack = () => {
    setStep((prev) => Math.max(1, prev - 1));
  };

  const renderStepContent = () => {
    switch (step) {
      case 1:
        return (
          <div className="space-y-4">
            <div className="space-y-2 rounded-2xl border p-4 shadow-sm shadow-black/5">
              <p className="text-sm font-medium text-foreground">
                {t("persona.languages.nativeLabel", "What’s your native language?")}
              </p>
              <Select
                value={
                  state.languagePreferences.nativeLanguage ?? undefined
                }
                onValueChange={(value) =>
                  setState((prev) => ({
                    ...prev,
                    languagePreferences: { ...prev.languagePreferences, nativeLanguage: value },
                    subtitles: { ...prev.subtitles, nativeLanguage: value },
                  }))
                }
              >
                <SelectTrigger className="h-10 rounded-xl border">
                  <SelectValue placeholder={t("persona.subtitles.nativeLanguagePlaceholder", "Select language")} />
                </SelectTrigger>
                <SelectContent className="z-[120]">
                  {nativeLanguageOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2 rounded-2xl border p-4 shadow-sm shadow-black/5">
              <p className="text-sm font-medium text-foreground">
                {t("persona.languages.targetLabel", "Which language are you learning?")}
              </p>
              <Select
                value={state.languagePreferences.targetLanguage ?? "en"}
                onValueChange={(value) =>
                  setState((prev) => ({
                    ...prev,
                    languagePreferences: { ...prev.languagePreferences, targetLanguage: value },
                  }))
                }
              >
                <SelectTrigger className="h-10 rounded-xl border">
                  <SelectValue placeholder={t("persona.languages.targetPlaceholder", "Select target language")} />
                </SelectTrigger>
                <SelectContent className="z-[120]">
                  {targetLanguageOptions.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                {t("persona.languages.hint", "We’ll use this to match lessons and level guidance.")}
              </p>
            </div>
          </div>
        );
      case 2:
        return (
          <div className="space-y-2">
            {englishLevelOptions.map((option) => (
              <SelectableCard
                key={option.value}
                label={option.label}
                description={option.description}
                badge={<Badge variant="secondary">{option.value}</Badge>}
                isSelected={state.englishLevel === option.value}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    englishLevel: option.value,
                    targetDifficultyRange: TARGET_DIFFICULTY_MAP[option.value],
                  }))
                }
              />
            ))}
            {state.targetDifficultyRange ? (
              <p className="text-xs text-muted-foreground">
                {t("persona.levels.rangeHint", "We’ll aim for videos in the {from}–{to} range.")
                  .replace("{from}", state.targetDifficultyRange[0])
                  .replace("{to}", state.targetDifficultyRange[1])}
              </p>
            ) : null}
          </div>
        );
      case 3:
        return (
          <div className="grid gap-2 sm:grid-cols-2">
            {goalOptions.map((option) => (
              <SelectableCard
                key={option.value}
                label={option.label}
                isSelected={state.goals.includes(option.value)}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    goals: toggleListValue(prev.goals, option.value),
                  }))
                }
              />
            ))}
          </div>
        );
      case 4:
        return (
          <div className="grid gap-2 sm:grid-cols-2">
            {topicOptions.map((option) => (
              <SelectableCard
                key={option.value}
                label={option.label}
                isSelected={state.topics.includes(option.value)}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    topics: toggleListValue(prev.topics, option.value),
                  }))
                }
              />
            ))}
          </div>
        );
      case 5:
        return (
          <div className="grid gap-2 sm:grid-cols-2">
            {formatOptions.map((option) => (
              <SelectableCard
                key={option.value}
                label={option.label}
                isSelected={state.formats.includes(option.value)}
                onClick={() =>
                  setState((prev) => ({
                    ...prev,
                    formats: toggleListValue(prev.formats, option.value),
                  }))
                }
              />
            ))}
          </div>
        );
      case 6:
        return (
          <div className="space-y-5">
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t("persona.sessionLength.label", "Session length")}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {sessionLengthOptions.map((option) => (
                  <SelectableCard
                    key={option.value}
                    label={option.label}
                    description={option.description}
                    isSelected={state.sessionLength === option.value}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        sessionLength: prev.sessionLength === option.value ? undefined : option.value,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between gap-2">
                <p className="text-sm font-medium text-foreground">
                  {t("persona.sessionFrequency.prompt", "How often do you want to practice?")}
                </p>
                <Badge variant="outline">{t("common.optional", "Optional")}</Badge>
              </div>
              <div className="grid gap-2 sm:grid-cols-3">
                {sessionFrequencyOptions.map((option) => (
                  <SelectableCard
                    key={option.value}
                    label={option.label}
                    isSelected={state.sessionFrequency === option.value}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        sessionFrequency: prev.sessionFrequency === option.value ? null : option.value,
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        );
      case 7:
        return (
          <div className="space-y-5">
            {/* <div className="space-y-3.5 rounded-2xl border p-4 shadow-sm shadow-black/5">
              <div className="flex items-center justify-between gap-4">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t("persona.subtitles.alwaysTitle", "Always show English subtitles")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t("persona.subtitles.alwaysDescription", "Keep captions on by default for every video.")}
                  </p>
                </div>
                <Switch
                  checked={state.subtitles.withSubtitles}
                  onCheckedChange={(checked) =>
                    setState((prev) => ({
                      ...prev,
                      subtitles: { ...prev.subtitles, withSubtitles: checked },
                    }))
                  }
                />
              </div>
              <div className="flex items-center justify-between gap-4 border-t pt-4">
                <div>
                  <p className="text-sm font-medium text-foreground">
                    {t("persona.subtitles.nativeTitle", "Show translation in my language when I need help")}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {t(
                      "persona.subtitles.nativeDescription",
                      "Toggle native-language support whenever you get stuck.",
                    )}
                  </p>
                </div>
                <Switch
                  checked={state.subtitles.nativeLanguageSupport}
                  onCheckedChange={(checked) =>
                    setState((prev) => ({
                      ...prev,
                      subtitles: { ...prev.subtitles, nativeLanguageSupport: checked },
                    }))
                  }
                />
              </div>
            </div> */}
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">
                {t("persona.explanations.title", "Explanations you want to see")}
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {explanationPreferences.map((option) => (
                  <SelectableCard
                    key={option.value}
                    label={option.label}
                    isSelected={state.subtitles.explanationPreferences.includes(option.value)}
                    onClick={() =>
                      setState((prev) => ({
                        ...prev,
                        subtitles: {
                          ...prev.subtitles,
                          explanationPreferences: toggleListValue(
                            prev.subtitles.explanationPreferences,
                            option.value,
                          ) as ExplanationPreference[],
                        },
                      }))
                    }
                  />
                ))}
              </div>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  const stepInfo = stepMetadata[step - 1];
  const progressLabel = t("persona.progress.label", "Step {current} of {total}")
    .replace("{current}", String(step))
    .replace("{total}", String(totalSteps));
  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      {trigger ? <DialogTrigger asChild>{trigger}</DialogTrigger> : null}
      <DialogContent className="gap-0 p-0">
        <div className="rounded-t-[32px] border-b border-border/60 bg-gradient-to-b from-background/90 to-background px-6 pb-6 pt-5 sm:rounded-t-[38px]">
          <DialogHeader className="text-left">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground/80">
              {stepInfo.eyebrow}
            </p>
            <DialogTitle className="text-2xl font-semibold leading-tight text-foreground">
              {stepInfo.title}
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed text-muted-foreground">
              {stepInfo.description}
            </DialogDescription>
          </DialogHeader>
          <div className="mt-5 flex gap-2" aria-label={progressLabel}>
            {Array.from({ length: totalSteps }).map((_, index) => {
              const isCompleted = index + 1 <= step;
              return (
                <span
                  key={index}
                  className={cn(
                    "h-2 flex-1 rounded-full",
                    isCompleted ? "bg-black" : "bg-muted",
                  )}
                />
              );
            })}
          </div>
        </div>
        <div className="px-4 py-4">{renderStepContent()}</div>
        <DialogFooter className="rounded-b-[32px] border-t border-border/60 bg-muted/10 px-6 py-4 sm:items-center sm:justify-between sm:rounded-b-[38px]">
          {/* <DialogClose asChild>
            <Button variant="ghost" size="sm">
              {t("persona.actions.skip", "Skip for now")}
            </Button>
          </DialogClose> */}
          <div className="flex flex-1 items-center justify-end gap-2 sm:flex-none">
            {step > 1 ? (
              <Button
                variant="outline"
                className="border-black text-black hover:bg-black/5"
                onClick={handleBack}
              >
                {t("persona.actions.back", "Back")}
              </Button>
            ) : null}
            <Button
              className="bg-black text-white hover:bg-black/80"
              onClick={handleContinue}
              disabled={isSaving}
            >
              {isSaving ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  {t("persona.actions.saving", "Saving...")}
                </>
              ) : step === totalSteps ? (
                t("persona.actions.finish", "Finish")
              ) : (
                t("persona.actions.continue", "Continue")
              )}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogOverlay,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
  PersonaOnboardingDialog,
};
